import { Fragment, useLayoutEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DoneIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import styles from './SiteBanner.module.scss';
import { SiteBanner as SiteBannerAtom } from '@state';
import { useSiteBanner } from '@hooks';
import { CHIP_TYPE, TSiteBannerConfig } from '@types';

const TONE_CLASS: Record<CHIP_TYPE, string> = {
	[CHIP_TYPE.INFO]: styles.info,
	[CHIP_TYPE.WARN]: styles.warn,
	[CHIP_TYPE.SUCCESS]: styles.success,
	[CHIP_TYPE.ERROR]: styles.error,
	[CHIP_TYPE.NEUTRAL]: styles.info,
	[CHIP_TYPE.BRAND]: styles.brand,
};

const TONE_ACTION_CLASS: Record<CHIP_TYPE, string> = {
	[CHIP_TYPE.INFO]: styles.actionButtonInfo,
	[CHIP_TYPE.WARN]: styles.actionButtonWarn,
	[CHIP_TYPE.SUCCESS]: styles.actionButtonSuccess,
	[CHIP_TYPE.ERROR]: styles.actionButtonError,
	[CHIP_TYPE.NEUTRAL]: styles.actionButtonInfo,
	[CHIP_TYPE.BRAND]: styles.actionButtonBrand,
};

const TONE_ICON: Record<CHIP_TYPE, typeof InfoIcon> = {
	[CHIP_TYPE.INFO]: InfoIcon,
	[CHIP_TYPE.WARN]: WarningIcon,
	[CHIP_TYPE.SUCCESS]: DoneIcon,
	[CHIP_TYPE.ERROR]: ErrorIcon,
	[CHIP_TYPE.NEUTRAL]: InfoIcon,
	[CHIP_TYPE.BRAND]: InfoIcon
};

type TBannerInnerProps = {
	banner: TSiteBannerConfig;
	onDismiss: () => void;
}

const SiteBannerInner = (props: TBannerInnerProps) => {
	const { banner, onDismiss } = props;
	const { message, actions = [], dismissible = true, tone = CHIP_TYPE.INFO, onDismiss: onConfigDismiss } = banner;
	const containerRef = useRef<HTMLDivElement>(null);
	const ToneIcon = TONE_ICON[tone] ?? InfoIcon;
	const actionClass = TONE_ACTION_CLASS[tone] ?? styles.actionButtonInfo;

	const handleDismiss = () => {
		onConfigDismiss?.();
		onDismiss();
	};

	useLayoutEffect(() => {
		const el = containerRef.current;
		const target = document.getElementById('mainLayout');
		if (!el || !target) return;

		const apply = (height: number) => {
			target.style.setProperty('--site-banner-height', `${height}px`);
		};

		apply(el.getBoundingClientRect().height);

		const observer = new ResizeObserver((entries) => {
			const height = entries[0]?.contentRect.height ?? 0;
			apply(height);
		});
		observer.observe(el);

		return () => {
			observer.disconnect();
			target.style.setProperty('--site-banner-height', '0px');
		};
	}, []);

	return (
		<div
			ref={containerRef}
			data-testid={'@Ofload-SiteBanner'}
			className={`${styles.container} ${TONE_CLASS[tone] ?? styles.info}`}
			role={'status'}
			aria-live={'polite'}
		>
			<div data-testid={'@Ofload-SiteBanner-Message'} className={styles.message}>
				<ToneIcon className={styles.toneIcon} />
				<div className={styles.messageContent}>{message}</div>
			</div>
			<div className={styles.actions}>
				{actions.map((action, i) => (
					<Fragment key={i}>
						{i > 0 && <span aria-hidden={true} className={styles.divider} />}
						<button
							data-testid={`@Ofload-SiteBanner-Action-${i}`}
							className={`${styles.actionButton} ${actionClass}`}
							onClick={action.onClick}
							disabled={action.disabled}
						>
							{action.label}
						</button>
					</Fragment>
				))}
				{dismissible && (
					<button
						data-testid={'@Ofload-SiteBanner-Close'}
						className={`${styles.closeBtn} ${actionClass}`}
						onClick={handleDismiss}
						aria-label={'Dismiss banner'}
					>
						<CloseIcon />
					</button>
				)}
			</div>
		</div>
	);
}

export const SiteBanner = () => {
	const banner = useRecoilValue(SiteBannerAtom);
	const { hideBanner } = useSiteBanner();

	if (!banner) return null;

	return <SiteBannerInner banner={banner} onDismiss={hideBanner} />;
}
